<template>
	<div :class="sidebarClasses" :style="sidebarStyle">
		<div class="row titlebar">
			<div class="col col-auto">
				<span class="title">{{ sidebar.title }}</span>
				<span v-if="sidebar.titleAdditional" class="title-info">{{ sidebar.titleAdditional }}</span>
			</div>
			<div class="col text-right">
				<button type="button" class="close" @click="closeSidebar">&times;</button>
			</div>
		</div>
		<div class="row content" ref="content">
			<div class="col col-12">
				<component :is="sidebar.view" :data="sidebar.outputData" ref="component"></component>
			</div>
		</div>
		<div class="row bottombar">
			<div class="col">
				<div class="row info" v-if="sidebar.info">
					<div class="col col-auto">
						<b-alert class="info_" :variant="sidebar.info.variant" show>
							<span class="_title" v-if="sidebar.info.title">{{ sidebar.info.title }}:</span>
							<span class="_text">{{ sidebar.info.text }}</span>
						</b-alert>
					</div>
				</div>
				<div class="row controls">
					<div class="col col-auto">
						<b-button :class="[ 'apply-btn', { applying } ]"
						          :variant="applyButton.variant"
						          @click="applyChanges">
							<span>{{ applyButton.text }}</span>
							<stretch v-if="applying" class="loader" background="#fff" size="25px">
							</stretch>
						</b-button>
						<b-button class="reset"
						          v-if="resetButton.visible && applyButton.applied"
						          variant="danger"
						          @click="triggerReset">
							<span>Zurücksetzen</span>
						</b-button>
						<b-button class="cancel"
						          v-if="!applyButton.applied"
						          @click="closeSidebar">
							Abbrechen
						</b-button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import throttle from 'lodash.throttle';

	import { Stretch } from 'vue-loading-spinner';

	import ErcoSidebarBasisgenus from '@/components/molecules/ErcoSidebar/Basisgenus';
	import ErcoSidebarDateTimePicker from '@/components/molecules/ErcoSidebar/DateTimePicker';
	import ErcoSidebarTextInput from '@/components/molecules/ErcoSidebar/TextInput';
	import ErcoSidebarTabbedTextInput from '@/components/molecules/ErcoSidebar/TabbedTextInput';
	import ErcoSidebarTriad from '@/components/molecules/ErcoSidebar/Triad';
	import ErcoSidebarLinkedProducts from '@/components/molecules/ErcoSidebar/LinkedProducts';
	import ErcoSidebarTags from '@/components/molecules/ErcoSidebar/Tags';
	import ErcoSidebarImageLoader from '@/components/molecules/ErcoSidebar/ImageLoader';
	import ErcoSidebarDropdown from '@/components/molecules/ErcoSidebar/Dropdown';
	import ErcoSidebarImageEditor from '@/components/molecules/ErcoSidebar/ImageEditor';
	import ErcoSidebarSiteEditor from '@/components/molecules/ErcoSidebar/SiteEditor';
	import ErcoSidebarTemplateSelection from '@/components/molecules/ErcoSidebar/TemplateSelection';
	import ErcoSidebarLastChanges from '@/components/molecules/ErcoSidebar/LastChanges';

	const headerMenuHeight = 113;

	export default {
		name: 'ErcoSidebarOrganism',

		props: {
			sidebar: {
				type: Object,
				required: true,
			},
		},

		data() {
			return {
				applying: false,
				applyError: false,
				throttledScrollListener: null,
			};
		},

		computed: {
			sidebarClasses() {
				const classes = {
					'container-fluid': true,
					'sidebar-wrapper': true,
					'-fixed': this.sidebar.fixed,
				};

				classes[this.sidebar.view] = true;
				return classes;
			},

			sidebarStyle() {
				const styleAttrValue =
					Object.keys(this.sidebar.styles)
						.filter(cssProp => this.sidebar.styles[cssProp])
						.map((cssProp) => {
							let fixedCssProp = cssProp;
							const cssPropParts =
								cssProp.match(/([A-Za-z][a-z]*)/g);
							if (cssPropParts) {
								fixedCssProp = cssPropParts.map(part => part.toLowerCase()).join('-');
							}
							const cssPropValue = this.sidebar.styles[cssProp];
							return `${fixedCssProp}: ${cssPropValue};`;
						});

				return styleAttrValue.join(' ');
			},

			applyButton() {
				let buttonData = {
					variant: 'success',
					text: 'Bestätigen',
					applied: false,
				};

				if (this.applyError) {
					buttonData = {
						variant: 'primary',
						text: 'Wiederholen',
						applied: false,
					};
				} else if (this.$store.state.sidebar.applied) {
					buttonData = {
						variant: '',
						text: 'Schließen',
						applied: true,
					};
				}

				return buttonData;
			},

			resetButton() {
				return Object.assign({}, this.$store.state.sidebar.resetButton);
			},
		},

		created() {
			window.removeEventListener('scroll', this.throttledScrollListener);
			this.throttledScrollListener = throttle(this.handleScroll.bind(this), 15);
			window.addEventListener('scroll', this.throttledScrollListener);
		},

		mounted() {
			this.$el.style.transform = null;
			this.repositionSidebar();
		},

		beforeDestroy() {
			window.removeEventListener('scroll', this.throttledScrollListener);
		},

		methods: {
			applyChanges() {
				if (this.$store.state.sidebar.applied) {
					this.closeSidebar();
					return;
				}

				if (this.$refs.component && this.$refs.component.handleApply) {
					this.$refs.component.handleApply();
				}

				const actionName = this.$store.state.sidebar.context.dispatchActionName;
				const value = this.$store.state.sidebar.context.value;

				this.applying = true;

				this.$store.dispatch(actionName, value)
					.then(() => {
						if (this.sidebar.closeOnSave) {
							this.closeSidebar();
						}

						/* Sofern das Setzen der Werte über die API erfolgreich verlief,
							soll festgehalten werden, dass der Reset nach einer
							Änderung erfolgt ist. */
						if (this.$store.state.sidebar.revert === true) this.$store.dispatch('revertSidebar');
						else this.$store.dispatch('updateSidebar', { applied: true });

						this.applying = false;
						this.applyError = false;
					})
					.catch(() => {
						 /* TODO: Use (better) logging */
						this.applying = false;
						this.applyError = true;
					});
			},

			closeSidebar() {
				this.$store.dispatch('resetSidebar');
			},

			triggerReset() {
				if (this.$refs.component && this.$refs.component.triggerReset) {
					this.$refs.component.triggerReset();
				}
			},

			handleScroll() {
				this.repositionSidebar();
			},

			repositionSidebar() {
				if (!this.sidebar.fixed) {
					return;
				}

				const el = document.body.parentElement.scrollTop ?
							document.body.parentElement :
							document.body;
				if (el.scrollTop < headerMenuHeight) {
					const diff = headerMenuHeight - el.scrollTop;
					this.$el.style.height = `calc(100% - ${diff}px)`;
					this.$el.style.transform = `translateY(${diff}px)`;
				} else {
					this.$el.style.height = null;
					this.$el.style.transform = null;
				}
			},
		},

		components: {
			Stretch,

			ErcoSidebarBasisgenus,
			ErcoSidebarDateTimePicker,
			ErcoSidebarTextInput,
			ErcoSidebarTabbedTextInput,
			ErcoSidebarTriad,
			ErcoSidebarLinkedProducts,
			ErcoSidebarTags,
			ErcoSidebarImageLoader,
			ErcoSidebarDropdown,
			ErcoSidebarImageEditor,
			ErcoSidebarSiteEditor,
			ErcoSidebarTemplateSelection,
			ErcoSidebarLastChanges,
		},
	};
</script>

<style lang="scss">
	.sidebar-wrapper {
		background-color: rgba(255, 255, 255, 1);
		box-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
		right: 0;
		width: 55%;
		z-index: 999999;
		display: flex;
		flex-direction: column;
		height: 100%;
		top: 0;

		&:not(.-fixed) {
			position: absolute !important;
		}

		&.-fixed {
			position: fixed !important;

			& > .bottombar {
				flex: none;
			}
		}

		&.erco-sidebar-dropdown {
			& > .content {
				overflow-y: visible;
			}
		}

		& > .titlebar {
			background-color: rgba(204, 204, 204, .2);
			border-bottom: 1px solid rgba(204, 204, 204, 1);
			padding: 0.3rem 0.5rem;
			min-height: 2.1rem;

			.title {
				font-weight: bold;
			}

			.title-info {
				color: rgba(129, 138, 145, 1);
				margin-left: 0.5rem;
			}

			& .close {
				border: 1px solid rgba(204, 204, 204, 1);
				border-radius: 10%;
				padding: 0.2rem 0.35rem;
				font-size: 1rem;
			}
		}

		& > .content {
			flex: 0 1 auto;
			overflow-y: auto;
		}

		& > .bottombar {
			flex: 1 0 auto;
			padding: 0.5rem;
			padding-top: 1.5rem;
			padding-bottom: 1.5rem;

			.info_ {
				& > ._title {
					font-weight: bold;
				}
			}

			.controls {
				.apply-btn {
					overflow: hidden;

					&.applying {
						padding-right: 60px;
					}

					& > .loader {
						display: inline-block;
						position: absolute !important;
						margin: -2px 0 0 15px;
					}
				}
			}
		}
	}
</style>



// WEBPACK FOOTER //
// ErcoSidebar.vue?730b401c